export type PaymentNoticeMatchStatus =
  | "MATCHED"
  | "MATCHED_BY_ALIAS"
  | "MATCHED_MANUALLY"
  | "MATCHED_BY_AI"
  | "AMBIGUOUS"
  | "UNMATCHED";

export const PAYMENT_NOTICE_MATCH_STATUS_LABELS: Record<
  PaymentNoticeMatchStatus,
  string
> = {
  MATCHED: "자동 매칭",
  MATCHED_BY_ALIAS: "별칭 매칭",
  MATCHED_MANUALLY: "수동 매칭",
  MATCHED_BY_AI: "AI 매칭",
  AMBIGUOUS: "매칭 실패 (후보 다수)",
  UNMATCHED: "매칭 실패"
};

export type PaymentNoticeDocType =
  | "NATIONAL_TAX"
  | "LOCAL_TAX"
  | "TAX_RECEIPT"
  | "OTHER";

export const PAYMENT_NOTICE_DOC_TYPE_LABELS: Record<
  PaymentNoticeDocType,
  string
> = {
  NATIONAL_TAX: "국세",
  LOCAL_TAX: "지방세",
  // 원천세 신고서 접수증(docType: TAX_RECEIPT)
  TAX_RECEIPT: "접수증",
  OTHER: "기타"
};

export interface PaymentNoticeAccount {
  bank: string;
  no: string;
}

// 원천세 신고서 접수증(docType: TAX_RECEIPT) 전용 상세 항목.
// 그 외 문서 유형에서는 PaymentNotice.taxReceipt가 null이다.
export interface TaxReceiptInfo {
  receiptNo: string | null;
  receivedAt: string | null;
  receiptResult: string | null;
  reportType: string | null;
  reportCategory: string | null;
  frequency: string | null;
  paymentPeriod: string | null;
  submitPeriod: string | null;
  totalPayAmount: number | null;
  // 소득세 등 납부세액 (PaymentNotice.amount와 동일 값)
  incomeTaxAmount: number | null;
  // 농어촌특별세 납부세액
  ruralTaxAmount: number | null;
  // 차월이월환급세액
  carriedOverRefund: number | null;
  refundRequested: boolean | null;
  // 환급신청금액
  refundRequestAmount: number | null;
}

export interface PaymentNotice {
  id: string;
  organizationId: string;
  businessId: string | null;
  matchStatus: PaymentNoticeMatchStatus;
  matchNote: string | null;
  originalFileName: string;
  contentType: string;
  downloadUrl: string;
  docType: string;
  businessNo: string;
  companyName: string;
  taxItem: string;
  incomeType: string;
  period: string;
  dueDate: string;
  amount: number;
  epayNo: string;
  accounts: PaymentNoticeAccount[];
  confidence: string;
  confidenceReason: string | null;
  // docType이 TAX_RECEIPT인 경우에만 채워짐, 그 외에는 null
  taxReceipt: TaxReceiptInfo | null;
  createdDate: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface SimpleBusiness {
  id: string;
  name: string;
  regNumber: string;
}

export interface BusinessWithPaymentNoticeCount {
  id: string;
  name: string;
  regNumber: string;
  incharge?: string;
  cluster?: string;
  subCluster?: string;
  // 사업자 소유 기관 대표 사용자(User)의 휴대전화번호. 읽기 전용
  userMobileNo?: string | null;
  // Business.payrollManagerMobileNo (급여담당자 연락처). 카카오 알림톡 발신 우선 번호로 쓰이며,
  // PRO에서 쓰기 가능한 필드는 이것뿐. (구 Business.kakaoMobileNo 필드는 제거됨)
  kakaoMobileNo?: string | null;
  // 조회한 귀속연월(period)에 대한 카카오 알림톡 발송 일시
  kakaoSentAt?: string | null;
  nationalTaxCount: number;
  localTaxCount: number;
  // 원천세 신고서 접수증(docType: TAX_RECEIPT) 건수. totalCount에도 합산됨
  taxReceiptCount: number;
  otherCount: number;
  totalCount: number;
}

export interface BusinessWithPaymentNotices {
  id: string;
  name: string;
  regNumber: string;
  paymentNotices: PaymentNotice[];
}

// 2.8 납부서 카카오 알림톡 발송 이력 1건 (Business.kakaoHistory[period] 요소, 발송 순서대로)
export interface KakaoNotificationLog {
  sentAt: string;
  // 사용한 알림톡 템플릿 ID (KA01TP...)
  templateId: string;
  // 실제 발송한 수신 번호 (발신 번호 우선순위로 해석된 최종 번호)
  recipientMobileNo: string | null;
  // 템플릿 치환 변수. 키는 "#{BNM}" 처럼 중괄호를 포함한 형태
  variables: Record<string, string>;
}

// 파일 1개당 업로드 API 호출 결과 - 성공 시 생성된 PaymentNotice, 실패 시 에러 메시지
export interface PaymentNoticeUploadResult {
  fileName: string;
  notice: PaymentNotice | null;
  errorMessage?: string;
  // 업로드가 실제로 시도조차 되지 않고 중단(취소)된 건인지 여부 - 실패(errorMessage)와
  // 구분해서 집계/표시하기 위한 플래그
  cancelled?: boolean;
}

// 수동 매칭 확정 요청 1건
export interface PaymentNoticeMatchRequest {
  paymentNoticeId: string;
  businessId: string;
}

// 수동 매칭 확정 응답 1건 - 요청 배열과 같은 순서·같은 건수로 반환되며 건별로 성공 여부가 갈린다
export interface PaymentNoticeMatchResult {
  paymentNoticeId: string;
  success: boolean;
  paymentNotice: PaymentNotice | null;
  errorMessage: string | null;
}
